import type { TFunction } from "i18next";

import {
  DatevExport,
  handleDatevExport,
  initialDatevExportValues,
} from "./commands/DatevExport";
import { handleDeleteEndlessDatevExports } from "./commands/DeleteEndlessDatevExports";
import {
  ExportEURTransactions,
  handleEURTransactions,
  initialExportEURTransactionsValues,
} from "./commands/ExportEURTransactions";
import {
  ExportTransactions,
  handleExportTransactions,
} from "./commands/ExportTransactions";
import {
  ResetDeclarationApproval,
  handleResetDeclarationApproval,
  initialResetDeclarationApprovalValues,
} from "./commands/ResetDeclarationApproval";
import {
  ResetUserTaxCase,
  handleResetUserTaxCase,
  initialResetUserTaxCaseValues,
} from "./commands/ResetUserTaxCase";
import {
  handleGenerateInviteLink,
  renderInviteLinkSuccess,
} from "./commands/GenerateInviteLink";
import {
  ExportSmartaBankingTransactions,
  handleExportSmartaBankingTransactions,
} from "./commands/ExportSmartaBankingTransactions";
import {
  Command,
  CommandConfig,
  CommandFormValues,
  CommandMethod,
  CommandOption,
} from "./types";

export const config: Record<Command, CommandConfig> = {
  [Command.ExportTransactions]: {
    labelKey: "command.exportTransactions.label",
    component: ExportTransactions,
    method: handleExportTransactions,
  },
  [Command.DeleteEndlessDatevExports]: {
    labelKey: "command.deleteEndlessDatevExports.label",
    method: handleDeleteEndlessDatevExports,
  },
  [Command.ExportEURTransactions]: {
    labelKey: "command.exportEURTransactions.label",
    component: ExportEURTransactions,
    method: handleEURTransactions,
    initialValues: initialExportEURTransactionsValues,
  },
  [Command.DatevExport]: {
    labelKey: "command.datevExport.label",
    component: DatevExport,
    method: handleDatevExport,
    initialValues: initialDatevExportValues,
  },
  [Command.ResetDeclarationApproval]: {
    labelKey: "command.resetDeclarationApproval.label",
    component: ResetDeclarationApproval,
    method: handleResetDeclarationApproval,
    initialValues: initialResetDeclarationApprovalValues,
  },
  [Command.ResetUserTaxCase]: {
    labelKey: "command.resetUserTaxCase.label",
    component: ResetUserTaxCase,
    method: handleResetUserTaxCase,
    initialValues: initialResetUserTaxCaseValues,
  },
  [Command.GenerateInviteLink]: {
    labelKey: "command.generateInviteLink.label",
    method: handleGenerateInviteLink,
    onSuccess: renderInviteLinkSuccess,
  },
  [Command.ExportSmartaBankingTransactions]: {
    labelKey: "command.exportSmartaBankingTransactions.label",
    component: ExportSmartaBankingTransactions,
    method: handleExportSmartaBankingTransactions,
  },
};

export const getCommandOptions = (
  t: TFunction<"CommandHub"> | (() => string)
): CommandOption[] => {
  return Object.entries(config).map(([command, { labelKey }]) => ({
    label: t(labelKey),
    value: command as Command,
  }));
};

export const getCommandComponent = (
  command: Command
): React.ComponentType | null => {
  return config[command].component ?? null;
};

export const getCommandMethod = (command: Command): CommandMethod => {
  const commandMethod = config[command].method;
  if (!commandMethod) {
    throw new Error(`Command "${command}" not implemented yet.`);
  }
  return commandMethod;
};

export const getCommandInitialValues = (
  command: Command
): Partial<CommandFormValues> => {
  return config[command].initialValues ?? {};
};

export const getCommandSuccessHandler = (command: Command) => {
  return config[command].onSuccess ?? null;
};
